// OrdersPage.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Grid,
  Button,
  TextField,
  Chip,
  useTheme,
  Avatar,
  InputAdornment,
  keyframes,
  styled,
  alpha,
  useMediaQuery,
  Stack,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import DiscountIcon from "@mui/icons-material/Discount";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ComputerIcon from "@mui/icons-material/Computer";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  cancelOrder,
  confirmEtransferPayment,
  recordManualPayment,
  resendPaymentLink,
  fetchOrders,
  fetchOrderCounts,
  Order,
  processProviderRefund,
  recordManualRefund,
  sendReceipt,
} from "../api/orderApi";
import { useNavigate } from "react-router-dom";
import { getFulfillmentByOrderId } from "../api/fulfillmentApi";
import { formatAttributes as fmtAttrs } from "../utils/variantAttributes";
import ConfirmManualPaymentDialog from "../components/orders/ConfirmManualPaymentDialog";
import OrderActivityTimeline from "../components/orders/OrderActivityTimeline";
import FeedbackToast, { type FeedbackToastState } from "../components/FeedbackToast";
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';


const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

type ChipTone = "default" | "success" | "warning" | "info" | "error" | "secondary";

function normalizeDeliveryStatus(s?: string | null) {
  return (s || "NEW").toUpperCase();
}

function deliveryStatusChipProps(statusRaw?: string | null): { label: string; color: ChipTone } {
  const s = normalizeDeliveryStatus(statusRaw);

  switch (s) {
    case "NEW":
      return { label: "Delivery: NEW", color: "warning" };
    case "ASSIGNED":
      return { label: "Delivery: ASSIGNED", color: "info" };
    case "EN_ROUTE":
      return { label: "Delivery: EN ROUTE", color: "info" };
    case "NEARBY":
      return { label: "Delivery: NEARBY", color: "info" };
    case "DELIVERED":
      return { label: "Delivery: DELIVERED", color: "success" };
    case "FAILED":
      return { label: "Delivery: FAILED", color: "error" };
    case "CANCELLED":
      return { label: "Delivery: CANCELLED", color: "error" };
    default:
      return { label: `Delivery: ${s}`, color: "default" };
  }
}

function fulfillmentStatusChipProps(statusRaw?: string | null): { label: string; color: ChipTone } {
  const s = (statusRaw || "NEW").toUpperCase();

  switch (s) {
    case "NEW":
      return { label: "Fulfillment: NEW", color: "warning" };
    case "PACKING":
      return { label: "Fulfillment: PACKING", color: "info" };
    case "READY":
      return { label: "Fulfillment: READY", color: "info" };
    case "SHIPPED":
      return { label: "Fulfillment: SHIPPED", color: "success" };
    case "OUT_FOR_DELIVERY":
      return { label: "Fulfillment: OUT FOR DELIVERY", color: "secondary" };
    case "DELIVERED":
      return { label: "Fulfillment: DELIVERED", color: "success" };
    case "CANCELLED":
      return { label: "Fulfillment: CANCELLED", color: "error" };
    default:
      return { label: `Fulfillment: ${s}`, color: "default" };
  }
}

function paymentBalanceChipProps(order: Order): { label: string; color: ChipTone } | null {
  const balance = order.payment_balance;

  if (!balance) return null;

  if (balance.paymentBalanceStatus === "balance_due" && Number(balance.balanceDue || 0) > 0) {
    return {
      label: `Balance Due: $${Number(balance.balanceDue).toFixed(2)}`,
      color: "warning",
    };
  }

  if (balance.paymentBalanceStatus === "refund_due" && Number(balance.refundDue || 0) > 0) {
    return {
      label: `Refund Due: $${Number(balance.refundDue).toFixed(2)}`,
      color: "error",
    };
  }

  if (balance.paymentBalanceStatus === "balanced") {
    return {
      label: "Payment Balanced",
      color: "success",
    };
  }

  return null;
}

function getOrderFulfillmentType(order: Order) {
  return String(
    order.fulfillment_type ||
    (order as any).fulfillment_method ||
    (order.is_delivery ? "local_delivery" : "shipping")
  ).toLowerCase();
}

function shouldShowFulfillmentChip(order: Order) {
  return Boolean(order.fulfillment_id || order.fulfillment_status || order.is_online_order);
}

function isLocalDeliveryOrder(order: Order) {
  return getOrderFulfillmentType(order) === "local_delivery" || Boolean(order.is_delivery);
}

const ModernAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '12px!important',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.35),
    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)'
  },
  '&::before': {
    display: 'none'
  }
}));

// Single distinct accent reserved for wholesale orders (teal, so it never
// collides with the semantic success/warning/error/info chip colors).
const WHOLESALE_ACCENT = "#7C3AED";

/**
 * Unified status chip used across the order summary header: one size, subtle
 * tonal fill, pill shape. `tone` maps to the semantic theme palette colors
 * returned by deliveryStatusChipProps / fulfillmentStatusChipProps /
 * paymentBalanceChipProps; `accent` overrides for the wholesale chip.
 */
const StatusChip: React.FC<{
  label: string;
  tone?: ChipTone;
  accent?: string;
  icon?: React.ReactElement;
}> = ({ label, tone = "default", accent, icon }) => {
  const theme = useTheme();

  const palette =
    tone === "success" ? theme.palette.success :
    tone === "warning" ? theme.palette.warning :
    tone === "info" ? theme.palette.info :
    tone === "error" ? theme.palette.error :
    tone === "secondary" ? theme.palette.secondary :
    null;

  const base = accent ?? palette?.main ?? theme.palette.text.secondary;
  const text = accent
    ? accent
    : palette
      ? (theme.palette.mode === 'light' ? palette.dark : palette.light)
      : theme.palette.text.secondary;

  return (
    <Chip
      label={label}
      icon={icon}
      size="small"
      sx={{
        height: 22,
        borderRadius: 999,
        fontWeight: 600,
        fontSize: '0.6875rem',
        letterSpacing: '0.02em',
        color: text,
        backgroundColor: alpha(base, 0.12),
        '& .MuiChip-label': { px: 1 },
        '& .MuiChip-icon': { color: text, fontSize: '0.875rem', ml: 0.75 }
      }}
    />
  );
};

// Small copy-to-clipboard button for an order number. stopPropagation so it does
// not toggle the row's accordion; shows a brief "Copied!" confirmation.
const CopyOrderIdButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy order #"}>
      <IconButton
        size="small"
        aria-label="Copy order number"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard
            ?.writeText(value)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => {});
        }}
        sx={{ p: 0.25, ml: 0.25 }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 15 }} color="success" />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 15 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const ORDERS_PAGE_SIZE = 50;
  const [ordersLimit, setOrdersLimit] = useState(ORDERS_PAGE_SIZE);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [orderCounts, setOrderCounts] = useState<{ pending: number; to_fulfill: number }>({ pending: 0, to_fulfill: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    'all' | 'paid' | 'pending' | 'to_fulfill' | 'online' | 'delivery' | 'mail' | 'cancelled' | 'wholesale'
  >('all');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [confirmPaymentOrder, setConfirmPaymentOrder] = useState<Order | null>(null);
  const [confirmPaymentLoading, setConfirmPaymentLoading] = useState(false);

  const [refundOrder, setRefundOrder] = useState<Order | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundMode, setRefundMode] = useState<"provider" | "manual">("manual");
  const [refundDraft, setRefundDraft] = useState({
    amount: "",
    method: "manual",
    reason: "Order adjustment refund",
    note: "",
  });

  const [cancelOrderTarget, setCancelOrderTarget] = useState<Order | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDraft, setCancelDraft] = useState({
    refund_mode: "none" as "none" | "provider" | "manual",
    manual_refund_method: "manual",
    amount: "",
    reason: "Customer requested cancellation",
    force: false,
  });

  const [feedback, setFeedback] = useState<FeedbackToastState>({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();

  const handleFulfill = async (orderId: number) => {
    try {
      const f = await getFulfillmentByOrderId(orderId);
      navigate(`/fulfillments/${f.fulfillment_id}`);
    } catch (err) {
      console.error("Failed to open fulfillment:", err);
      alert("No fulfillment record found for this order.");
    }
  };

  const handleConfirmManualPayment = async (
    payload: {
      amount_collected: number;
      payment_method: "cash" | "etransfer" | "manual_card" | "other" | "store_credit";
      note?: string;
    }
  ) => {
    if (!confirmPaymentOrder) return;

    setConfirmPaymentLoading(true);

    try {
      await recordManualPayment(
        confirmPaymentOrder.order_id,
        payload
      );

      await loadOrders();

      setConfirmPaymentOrder(null);

      setFeedback({
        open: true,
        type: "success",
        title: "Payment recorded",
        message: "Manual payment was recorded successfully.",
      });
    } catch (err: any) {
      console.error("Failed to record manual payment:", err);

      setFeedback({
        open: true,
        type: "error",
        title: "Payment failed",
        message:
          err?.response?.data?.error ||
          "Failed to record payment.",
      });
    } finally {
      setConfirmPaymentLoading(false);
    }
  };


  const handleResendPaymentLink = async (orderId: number) => {
    try {
      await resendPaymentLink(orderId);

      setFeedback({
        open: true,
        type: "success",
        title: "Payment link resent",
        message: "Payment link email was resent successfully.",
      });
    } catch (err: any) {
      console.error("Failed to resend payment link:", err);

      setFeedback({
        open: true,
        type: "error",
        title: "Resend failed",
        message:
          err?.response?.data?.error ||
          "Failed to resend payment link.",
      });
    }
  };

  const getRefundDue = (order: Order | null) => {
    return Number(
      order?.payment_balance?.refundDue ||
      order?.payments?.balance?.refundDue ||
      0
    );
  };

  const getPaidTotal = (order: Order | null) => {
    return Number(
      order?.payment_balance?.paidTotal ||
      order?.payments?.balance?.paidTotal ||
      0
    );
  };

  const isCancelledOrder = (order: Order | null) => {
    return String(order?.order_status || "").toLowerCase() === "cancelled";
  };

  const canCancelOrder = (order: Order | null) => {
    if (!order) return false;
    if (isCancelledOrder(order)) return false;

    const fulfillmentStatus = String(order.fulfillment_status || "").toUpperCase();
    const deliveryStatus = String(order.delivery_status || "").toUpperCase();

    if (["SHIPPED", "DELIVERED"].includes(fulfillmentStatus)) return false;
    if (deliveryStatus === "DELIVERED") return false;

    return true;
  };

  const canUseProviderRefund = (order: Order | null) => {
    if (!order) return false;

    const paymentMethod = String(order.payment_method || "").toLowerCase();
    const refundDue = getRefundDue(order);
    const paidTotal = getPaidTotal(order);

    if (paymentMethod === "paypal") return true;

    // Clover provider refund is only safe for full charge refunds.
    if (
      (paymentMethod === "clover" || paymentMethod === "card") &&
      refundDue > 0 &&
      Math.abs(refundDue - paidTotal) < 0.01
    ) {
      return true;
    }

    return false;
  };

  const openCancelDialog = (order: Order) => {
    const paymentMethod = String(order.payment_method || "").toLowerCase();
    const paidTotal = getPaidTotal(order);
    const providerAllowed =
      paidTotal > 0 &&
      (paymentMethod === "paypal" || paymentMethod === "clover" || paymentMethod === "card");

    setCancelOrderTarget(order);
    setCancelDraft({
      refund_mode: "none",
      manual_refund_method:
        paymentMethod === "clover" || paymentMethod === "card"
          ? "clover_manual"
          : paymentMethod === "etransfer"
            ? "etransfer"
            : paymentMethod === "cash"
              ? "cash"
              : "manual",
      amount: paidTotal > 0 ? paidTotal.toFixed(2) : "",
      reason: "Customer requested cancellation",
      force: false,
    });

    if (!providerAllowed && paidTotal > 0) {
      setCancelDraft((prev) => ({
        ...prev,
        refund_mode: "manual",
      }));
    }
  };

  const handleSubmitCancelOrder = async () => {
    if (!cancelOrderTarget) return;

    setCancelLoading(true);

    try {
      await cancelOrder(cancelOrderTarget.order_id, {
        refund_mode: cancelDraft.refund_mode,
        manual_refund_method: cancelDraft.manual_refund_method,
        reason: cancelDraft.reason,
        force: cancelDraft.force,
        amount:
          cancelDraft.refund_mode === "none"
            ? undefined
            : Number(cancelDraft.amount || 0),
      });

      await loadOrders();
      setCancelOrderTarget(null);

      setFeedback({
        open: true,
        type: "success",
        title: "Order cancelled",
        message:
          cancelDraft.refund_mode === "none"
            ? "Order was cancelled without refund."
            : "Order was cancelled and refund was recorded.",
      });
    } catch (err: any) {
      const requiresForce = err?.response?.status === 409 || err?.response?.data?.requires_force;

      if (requiresForce) {
        setCancelDraft((prev) => ({ ...prev, force: true }));

        setFeedback({
          open: true,
          type: "warning",
          title: "Confirm cancellation",
          message:
            err?.response?.data?.error ||
            "This order is already in progress. Click cancel order again to confirm.",
        });
        return;
      }

      setFeedback({
        open: true,
        type: "error",
        title: "Cancellation failed",
        message: err?.response?.data?.error || "Failed to cancel order.",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const openRefundDialog = (order: Order) => {
    const refundDue = getRefundDue(order);
    const paymentMethod = String(order.payment_method || "").toLowerCase();

    const providerAllowed = canUseProviderRefund(order);
    const defaultMode: "provider" | "manual" = providerAllowed ? "provider" : "manual";

    const safeManualMethod =
      paymentMethod === "clover" || paymentMethod === "card"
        ? "clover_manual"
        : paymentMethod === "etransfer"
          ? "etransfer"
          : "manual";

    setRefundOrder(order);
    setRefundMode(defaultMode);
    setRefundDraft({
      amount: refundDue > 0 ? refundDue.toFixed(2) : "",
      method: defaultMode === "provider" ? paymentMethod : safeManualMethod,
      reason: "Order adjustment refund",
      note: "",
    });
  };

  const handleSubmitRefund = async () => {
    if (!refundOrder) return;

    const amount = Number(refundDraft.amount || 0);

    if (!amount || amount <= 0) {
      setFeedback({
        open: true,
        type: "error",
        title: "Invalid refund amount",
        message: "Refund amount must be greater than 0.",
      });
      return;
    }

    setRefundLoading(true);

    try {
      if (refundMode === "provider") {
        await processProviderRefund(refundOrder.order_id, {
          amount,
          reason: refundDraft.reason,
        });
      } else {
        await recordManualRefund(refundOrder.order_id, {
          amount,
          method: refundDraft.method,
          reason: refundDraft.reason,
          note: refundDraft.note,
        });
      }

      await loadOrders();
      setRefundOrder(null);
      setFeedback({
        open: true,
        type: "success",
        title: "Refund processed",
        message:
          refundMode === "provider"
            ? `Provider refund of $${amount.toFixed(2)} was processed successfully.`
            : `Manual refund of $${amount.toFixed(2)} was recorded successfully.`,
      });
    } catch (err: any) {
      console.error("Failed to process refund:", err);
      setFeedback({
        open: true,
        type: "error",
        title: "Refund failed",
        message: err?.response?.data?.error || "Failed to process refund.",
      });
    } finally {
      setRefundLoading(false);
    }
  };


  const formatAttributes = (attributes?: any) =>
    fmtAttrs(attributes, { sep: "\n", empty: "" });

  // Debounce the search box, then query the SERVER so search covers ALL orders,
  // not just the currently-loaded page.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // The payment-status chips map to a server-side filter so "pending"/"paid"
  // cover all orders too. Other chips (online/delivery/etc.) stay client-side.
  const serverStatus =
    selectedFilter === "pending"
      ? "pending"
      : selectedFilter === "paid"
      ? "paid"
      : selectedFilter === "to_fulfill"
      ? "to_fulfill"
      : undefined;

  const loadOrders = async (lim: number = ordersLimit) => {
    try {
      const fetchedOrders: Order[] = await fetchOrders({
        limit: lim,
        search: debouncedSearch || undefined,
        status: serverStatus,
      });
      setOrders(fetchedOrders);
      // A full page back means there may be older orders to load.
      setHasMoreOrders(fetchedOrders.length >= lim);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  useEffect(() => {
    loadOrders(ordersLimit);
    // Poll keeps the (possibly expanded) list fresh for new incoming orders.
    const interval = setInterval(() => loadOrders(ordersLimit), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersLimit, debouncedSearch, serverStatus]);

  // Filter-badge counts across ALL orders (independent of the current view/pagination).
  useEffect(() => {
    const load = () => fetchOrderCounts().then(setOrderCounts).catch(() => {});
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const generateOrderPDF = (order: Order) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Calculate order values
    const subtotal = order.subtotal || 0;
    const discountValue = Number(order.discount_value || 0);

    const discountType = String(order.discount_type || "").toLowerCase();
    const isPercent = discountType === "percentage" || discountType === "percent";

    const discountAmountRaw = isPercent
      ? (subtotal * discountValue) / 100
      : discountValue;

    // ✅ clamp so discount never exceeds subtotal
    const discountAmount = Math.min(subtotal, Math.max(0, discountAmountRaw));
    const loyaltyPointsUsed = order.loyalty_points_used || 0;
    const tax = order.tax || 0;
    const fee = Number(
      (order as any).fulfillment_fee ??
      (order.is_delivery ? order.delivery_fee : order.shipping_cost) ??
      0
    );

    const totalPrice = Math.max(
      0,
      subtotal - discountAmount + tax + fee - loyaltyPointsUsed
    );

    const feeLabel = order.is_delivery ? "Delivery Fee:" : "Shipping:";

    // Add logo
    const logoUrl = '/logo-fc.png';
    doc.addImage(logoUrl, 'PNG', 15, 10, 40, 15);

    // Header section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(40);
    doc.text(`ORDER RECEIPT`, 105, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`#${order.order_number}`, 105, 27, { align: 'center' });

    // Order details section
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(`Date: ${new Date(order.created_at).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 15, 40);

    doc.text(`Status:`, 15, 46);
    doc.setTextColor(order.payment_status === "paid" ? '#4CAF50' : '#FF9800');
    doc.text(`${order.payment_status === "paid" ? "PAID" : "PENDING"}`, 30, 46);

    doc.setTextColor(80);
    doc.text(`Method: ${order.payment_method?.toUpperCase() || "N/A"}`, 15, 52);

    // Shipping Information Section
    doc.setFontSize(11);
    doc.setTextColor(40);
    doc.setFont("helvetica", "bold");
    doc.text("SHIPPING INFORMATION", 15, 62);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(80);

    if (order.shipping_address) {
      doc.text(`Name: ${order.shipping_address.full_name || order.customer_name}`, 15, 68);
      doc.text(`Address: ${order.shipping_address.address || ''}`, 15, 74);

      const locationParts = [
        order.shipping_address.city,
        order.shipping_address.state,
        order.shipping_address.zip_code?.replace(/\s/g, '')
      ].filter(Boolean);

      doc.text(`Location: ${locationParts.join(', ').toLowerCase()}`, 15, 80);

      const phone = order.shipping_address.phone || order.customer_phone;
      const formattedPhone = phone && phone.length === 10 ?
        `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}` :
        phone || 'N/A';

      doc.text(`Phone: ${formattedPhone}`, 15, 86);
      doc.text(`Email: ${order.customer_email || 'N/A'}`, 15, 92);
    }

    // Items table
    autoTable(doc, {
      startY: order.shipping_address ? 97 : 70,
      head: [
        [
          {
            content: 'PRODUCT',
            styles: {
              fillColor: theme.palette.primary.main,
              textColor: 255,
              fontStyle: 'bold'
            }
          },
          {
            content: 'DETAILS',
            styles: {
              fillColor: theme.palette.primary.main,
              textColor: 255,
              fontStyle: 'bold'
            }
          },
          {
            content: 'PRICE',
            styles: {
              fillColor: theme.palette.primary.main,
              textColor: 255,
              fontStyle: 'bold',
              halign: 'right'
            }
          },
          {
            content: 'QTY',
            styles: {
              fillColor: theme.palette.primary.main,
              textColor: 255,
              fontStyle: 'bold',
              halign: 'center'
            }
          },
          {
            content: 'TOTAL',
            styles: {
              fillColor: theme.palette.primary.main,
              textColor: 255,
              fontStyle: 'bold',
              halign: 'right'
            }
          }
        ]
      ],
      body: order.items.map((item) => [
        {
          content: item.product_name,
          styles: { fontStyle: 'bold' }
        },
        item.variant_name || formatAttributes(item.attributes),
        {
          content: `$${item.price.toFixed(2)}`,
          styles: { halign: 'right' }
        },
        {
          content: item.quantity.toString(),
          styles: { halign: 'center' }
        },
        {
          content: `$${(item.quantity * item.price).toFixed(2)}`,
          styles: { halign: 'right' }
        },
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [221, 221, 221],
        lineWidth: 0.3
      },
      headStyles: {
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 25 },
        3: { cellWidth: 15 },
        4: { cellWidth: 25 }
      }
    });

    // Summary section
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(15, finalY - 5, 195, finalY - 5);

    doc.setFontSize(10);
    doc.setTextColor(80);

    const summaryRows = [
      { label: 'Subtotal:', value: `$${subtotal.toFixed(2)}` },
      { label: 'Discount:', value: `-$${discountAmount.toFixed(2)}` },
      { label: 'Loyalty Points:', value: `-$${loyaltyPointsUsed.toFixed(2)}` },
      { label: 'Tax:', value: `$${tax.toFixed(2)}` },
      { label: feeLabel, value: `$${fee.toFixed(2)}` }
    ];

    summaryRows.forEach((row, index) => {
      doc.text(row.label, 140, finalY + (index * 6));
      doc.text(row.value, 190, finalY + (index * 6), { align: 'right' });
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40);
    doc.text('TOTAL:', 140, finalY + 30);
    doc.text(`$${totalPrice.toFixed(2)}`, 190, finalY + 30, { align: 'right' });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text('Thank you for your business!', 105, 280, { align: 'center' });
    doc.text('Questions? Contact info@flavourchaser.co', 105, 285, { align: 'center' });

    doc.save(`Order_${order.order_number}_Receipt.pdf`);
  };

  const handleFilterClick = (
    filter: 'all' | 'paid' | 'pending' | 'to_fulfill' | 'online' | 'delivery' | 'mail' | 'cancelled' | 'wholesale'
  ) => {
    setSelectedFilter(filter);
    if (isMobile) {
      setFilterDrawerOpen(false);
    }
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, order: Order) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedOrder(null);
  };

  const handleMenuAction = (action: 'email' | 'print' | 'fulfill' | 'confirm-payment' | 'edit-order' | 'refund' | 'cancel') => {
    if (!selectedOrder) return;

    switch (action) {
      case 'email':
        sendReceipt(selectedOrder.order_id);
        break;
      case 'print':
        generateOrderPDF(selectedOrder);
        break;
      case 'fulfill':
        handleFulfill(selectedOrder.order_id);
        break;
      case 'confirm-payment':
        setConfirmPaymentOrder(selectedOrder);
        break;
      case 'edit-order':
        navigate(`/orders/${selectedOrder.order_id}/edit`);
        break;
      case 'refund':
        openRefundDialog(selectedOrder);
        break;
      case 'cancel':
        openCancelDialog(selectedOrder);
        break;
    }

    handleActionMenuClose();
  };

  const filteredOrders = orders.filter((order) => {
    // Server already searched all orders; this just gives instant feedback on the
    // loaded set and matches the same fields so nothing the server found is hidden.
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !q ||
      order.customer_name?.toLowerCase().includes(q) ||
      order.order_number.toLowerCase().includes(q) ||
      order.customer_email?.toLowerCase().includes(q) ||
      (order.customer_phone || "").toLowerCase().includes(q);

    const cancelled = isCancelledOrder(order);

    const matchesFilter =
      selectedFilter === "all" ||
      (selectedFilter === "cancelled" && cancelled) ||
      // Cancelled orders are excluded from the payment-status views — a
      // cancelled order is neither "completed" nor "pending payment".
      (selectedFilter === "paid" && order.payment_status === "paid" && !cancelled) ||
      (selectedFilter === "pending" && order.payment_status === "pending" && !cancelled) ||
      (selectedFilter === "to_fulfill" &&
        order.payment_status === "paid" &&
        !cancelled &&
        !["SHIPPED", "DELIVERED"].includes(String(order.fulfillment_status || "").toUpperCase()) &&
        String(order.delivery_status || "").toUpperCase() !== "DELIVERED") ||
      (selectedFilter === "online" && order.is_online_order) ||
      (selectedFilter === "wholesale" && order.is_wholesale) ||
      (selectedFilter === "delivery" && order.is_delivery) ||
      (selectedFilter === "mail" && !order.is_delivery && order.is_online_order);

    return matchesSearch && matchesFilter;
  });

  const FilterChips = () => {
    const filters: {
      label: string;
      value: typeof selectedFilter;
      icon?: React.ReactElement;
      accent?: string;
    }[] = [
      { label: 'All sales', value: 'all' },
      { label: 'Completed', value: 'paid' },
      { label: orderCounts.pending > 0 ? `Pending (${orderCounts.pending})` : 'Pending', value: 'pending' },
      { label: orderCounts.to_fulfill > 0 ? `To fulfill (${orderCounts.to_fulfill})` : 'To fulfill', value: 'to_fulfill', icon: <LocalShippingIcon fontSize="small" /> },
      { label: 'Online', value: 'online', icon: <ComputerIcon fontSize="small" /> },
      { label: 'Wholesale', value: 'wholesale', accent: WHOLESALE_ACCENT },
      { label: 'Delivery', value: 'delivery', icon: <LocalShippingIcon fontSize="small" /> },
      { label: 'Mail', value: 'mail', icon: <ReceiptIcon fontSize="small" /> },
      { label: 'Cancelled', value: 'cancelled' }
    ];

    return (
      <Box sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 0.5,
        mb: 3,
        p: 0.5,
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'light'
          ? theme.palette.grey[50]
          : alpha(theme.palette.common.white, 0.04)
      }}>
        {filters.map((filter) => {
          const active = selectedFilter === filter.value;
          const accent = filter.accent ?? theme.palette.primary.main;

          return (
            <Chip
              key={filter.value}
              label={filter.label}
              icon={filter.icon}
              onClick={() => handleFilterClick(filter.value)}
              sx={{
                height: 32,
                borderRadius: 2,
                px: 0.5,
                fontSize: '0.8125rem',
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s',
                backgroundColor: active ? accent : 'transparent',
                color: active
                  ? theme.palette.getContrastText(accent)
                  : theme.palette.text.secondary,
                boxShadow: active ? theme.shadows[1] : 'none',
                '& .MuiChip-icon': {
                  fontSize: '1rem',
                  ml: 0.75,
                  color: 'inherit'
                },
                '&:hover': {
                  backgroundColor: active ? accent : theme.palette.action.hover,
                  color: active
                    ? theme.palette.getContrastText(accent)
                    : theme.palette.text.primary
                }
              }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2, md: 3 },
      maxWidth: 1200,
      margin: '0 auto',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <Box sx={{
        mb: 3,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'background.paper',
        py: 2,
        boxSizing: 'border-box'
      }}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "center"}
        >
          <Typography variant="h4" sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            Order History
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={isMobile ? "center" : "flex-end"}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isMobile ? (
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2
                }}
              >
                <FilterListIcon />
              </IconButton>
            ) : null}

            <TextField
              variant="outlined"
              placeholder="Search receipts or customers..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 30,
                  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? '#eee' : '#333'
                  },
                  width: { xs: '100%', sm: 340 }
                }
              }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>

        {/* Desktop Filter Chips */}
        {!isMobile && <FilterChips />}
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            Filter Orders
          </Typography>
          <List>
            {[
              { label: 'All sales', value: 'all' },
              { label: 'Completed', value: 'paid' },
              { label: orderCounts.pending > 0 ? `Pending (${orderCounts.pending})` : 'Pending', value: 'pending' },
              { label: orderCounts.to_fulfill > 0 ? `To fulfill (${orderCounts.to_fulfill})` : 'To fulfill', value: 'to_fulfill' },
              { label: 'Online', value: 'online' },
              { label: 'Wholesale', value: 'wholesale' },
              { label: 'Delivery', value: 'delivery' },
              { label: 'Mail', value: 'mail' },
              { label: 'Cancelled', value: 'cancelled' }
            ].map((filter) => (
              <ListItemButton
                key={filter.value}
                onClick={() => handleFilterClick(filter.value as any)}
                selected={selectedFilter === filter.value}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    }
                  }
                }}
              >
                <ListItemText primary={filter.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Mobile Filter Indicator */}
      {isMobile && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Chip
            label={selectedFilter === 'all' ? 'All sales' :
              selectedFilter === 'paid' ? 'Completed' :
                selectedFilter === 'pending' ? 'Pending' :
                  selectedFilter === 'online' ? 'Online' :
                    selectedFilter === 'delivery' ? 'Delivery' : 'Mail'}
            color="primary"
            variant="filled"
            size="small"
            onClick={() => setFilterDrawerOpen(true)}
          />
        </Box>
      )}

      {/* Count — how many orders are currently shown */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Showing {filteredOrders.length} order{filteredOrders.length === 1 ? "" : "s"}
        {(debouncedSearch || serverStatus) ? " (filtered)" : ""}
        {hasMoreOrders ? " — load more below for older orders" : ""}
      </Typography>

      {/* Orders List */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: { xs: 2, sm: 3 }
      }}>
        {filteredOrders.map((order) => {
          const subtotal = order.subtotal || 0;
          const discountValue = Number(order.discount_value || 0);

          const discountType = String(order.discount_type || "").toLowerCase();
          const isPercent = discountType === "percentage" || discountType === "percent";

          const discountAmountRaw = isPercent
            ? (subtotal * discountValue) / 100
            : discountValue;

          // ✅ clamp so discount never exceeds subtotal
          const discountAmount = Math.min(subtotal, Math.max(0, discountAmountRaw));
          const fee = Number(
            (order as any).fulfillment_fee ??
            (order.is_delivery ? order.delivery_fee : order.shipping_cost) ??
            0
          );

          const totalPriceRaw =
            subtotal -
            discountAmount +
            (order.tax || 0) +
            fee -
            (order.loyalty_points_used || 0);

          // ✅ clamp so total never goes negative
          const totalPrice = Math.max(0, totalPriceRaw);

          return (
            <ModernAccordion
              key={order.order_id}
              sx={{ animation: `${fadeIn} 0.4s ease-out` }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                sx={{
                  p: { xs: 2, sm: 3 },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' }
                }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      width: { xs: 40, sm: 44 },
                      height: { xs: 40, sm: 44 },
                      fontSize: '0.8125rem',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  >
                    #{order.order_number.split('-').pop()}
                  </Avatar>

                  {/* DATE/TIME DISPLAY - ADD THIS HERE (DESKTOP) */}
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      minWidth: '100px',
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      pl: 2,
                      ml: 1
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.75rem' }}>
                      {new Date(order.created_at).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>

                  <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden'
                  }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {order.customer_name || "Walk-in Customer"}
                    </Typography>

                    {/* MOBILE DATE/TIME - ADD THIS HERE */}
                    <Box
                      sx={{
                        display: { xs: 'block', sm: 'none' },
                        width: '100%',
                        mb: 0.5
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })} • {new Date(order.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>

                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 0.75,
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Rest of the existing chips and order number */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, mr: 0.25 }}
                      >
                        #{order.order_number}
                      </Typography>

                      <CopyOrderIdButton value={order.order_number} />

                      {order.is_online_order && (
                        <StatusChip
                          label="ONLINE"
                          tone="info"
                          icon={<ComputerIcon />}
                        />
                      )}

                      {order.is_wholesale && (
                        <StatusChip
                          label={order.wholesale_tier_name ? `WHOLESALE · ${order.wholesale_tier_name}` : "WHOLESALE"}
                          accent={WHOLESALE_ACCENT}
                        />
                      )}
                      {isCancelledOrder(order) ? (
                        <StatusChip label="CANCELLED" tone="error" />
                      ) : (
                        <>
                          <StatusChip
                            label={String(order.payment_status || "pending").toUpperCase()}
                            tone={order.payment_status === "paid" ? "success" : "warning"}
                          />
                          {(() => {
                            const props = paymentBalanceChipProps(order);
                            return props ? (
                              <StatusChip label={props.label} tone={props.color} />
                            ) : null;
                          })()}
                        </>
                      )}

                      {shouldShowFulfillmentChip(order) && (
                        <>
                          {(() => {
                            const props = fulfillmentStatusChipProps(order.fulfillment_status);
                            const isLocalDelivery = isLocalDeliveryOrder(order);
                            const label = isLocalDelivery
                              ? props.label
                              : props.label.replace(/^Fulfillment:/, "Shipping:");

                            return <StatusChip label={label} tone={props.color} />;
                          })()}

                          {isLocalDeliveryOrder(order) &&
                            (() => {
                              const props = deliveryStatusChipProps(order.delivery_status);

                              return <StatusChip label={props.label} tone={props.color} />;
                            })()}
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Order total — visible at a glance on the right of each row */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      ml: 'auto',
                      pl: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Total
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      ${Number(order.total_price || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  {isMobile && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionMenuOpen(e, order);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                <Divider sx={{
                  mb: 3,
                  borderColor: 'divider',
                  borderWidth: '1px',
                  opacity: 0.5
                }} />

                {/* Shipping Information Section */}
                {order.shipping_address && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: theme.palette.mode === 'light'
                      ? theme.palette.grey[100]
                      : theme.palette.grey[800],
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}>
                      <LocalShippingIcon fontSize="small" /> SHIPPING DETAILS
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.primary">
                          <strong>Recipient:</strong> {order.shipping_address.full_name || order.customer_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-line" }}>
                          <strong>Address:</strong> {order.shipping_address.address}
                        </Typography>
                      </Grid>
                      {/* Two columns: address fields (City → Province → Postal) and
                          contact fields (Email → Phone). Each column's fields are
                          stacked so that on MOBILE (single column) they read in a
                          logical order instead of interleaving address and contact
                          rows (which put Email between City and Province). */}
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1.5}>
                          <Typography variant="body2" color="text.primary">
                            <strong>City:</strong> {order.shipping_address.city}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            <strong>Province:</strong> {order.shipping_address.state || 'Not provided'}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            <strong>Postal Code:</strong> {order.shipping_address.zip_code}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1.5}>
                          <Typography variant="body2" color="text.primary">
                            <strong>Email:</strong> {order.customer_email || 'Not provided'}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            <strong>Phone:</strong> {order.shipping_address.phone || order.customer_phone || 'Not provided'}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ✅ Order Note */}
                {order.note && String(order.note).trim().length > 0 && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.mode === "light"
                        ? theme.palette.warning.light + "22"
                        : theme.palette.warning.dark + "22",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                    >
                      📝 Order Note
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",     // keep line breaks
                        wordBreak: "break-word",
                        color: "text.primary",
                      }}
                    >
                      {String(order.note).trim()}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    ORDER ITEMS
                  </Typography>
                  {order.items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.action.hover,
                        flexWrap: { xs: 'wrap', sm: 'nowrap' }
                      }}
                    >
                      <Avatar
                        src={item.image_url}
                        variant="rounded"
                        sx={{
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          borderRadius: 1.5,
                          boxShadow: theme.shadows[1],
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        width: { xs: 'calc(100% - 60px)', sm: 'auto' }
                      }}>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {item.product_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            whiteSpace: 'pre-line',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {item.variant_name || formatAttributes(item.attributes)}
                        </Typography>
                      </Box>
                      <Box
                        textAlign="right"
                        sx={{
                          minWidth: { xs: '100%', sm: 'auto' },
                          mt: { xs: 1, sm: 0 },
                          flexShrink: 0
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          ${item.price.toFixed(2)} × {item.quantity}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ${(item.quantity * item.price).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <OrderActivityTimeline order={order} />

                <Grid container spacing={1.5} sx={{ mb: 3, mt: 1 }}>
                  <Grid item xs={6} display="flex" alignItems="flex-start" gap={1}>
                    <LocalAtmIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {order.payment_method?.includes('card') ? 'Credit Card' :
                          order.payment_method?.toUpperCase() || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="flex-start" gap={1}>
                    <LoyaltyIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Loyalty Used
                      </Typography>
                      <Typography color="primary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        -${order.loyalty_points_used?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="flex-start" gap={1}>
                    <DiscountIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Discount
                      </Typography>
                      <Typography color="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        -${discountAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="flex-start" gap={1}>
                    <ReceiptIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Tax
                      </Typography>
                      <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        ${order.tax?.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="flex-start" gap={1}>
                    <LocalShippingIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {order.is_delivery ? "Delivery Fee" : "Shipping Cost"}
                      </Typography>
                      <Typography color={order.is_delivery ? "primary.main" : "text.primary"}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        ${fee.toFixed(2)}
                      </Typography>
                      {order.is_delivery && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          Status: {order.delivery_status || "NEW"}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                {order.transactions && order.transactions.length > 0 && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === "light"
                          ? theme.palette.grey[50]
                          : theme.palette.grey[900],
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Payment History
                    </Typography>

                    <Stack spacing={1}>
                      {order.transactions.map((tx) => (
                        <Box
                          key={tx.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {String(tx.payment_method || "payment").toUpperCase()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(tx.created_at).toLocaleString()}
                            </Typography>
                          </Box>

                          <Typography variant="body2" fontWeight={700}>
                            ${Number(tx.amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {String(order.payment_method || "").startsWith("crypto") && order.payments?.data?.transaction && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === "light"
                          ? theme.palette.grey[50]
                          : theme.palette.grey[900],
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Crypto Payment
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        <strong>Network:</strong>{" "}
                        {String(order.payments?.data?.blockchain || "crypto").toUpperCase()}
                      </Typography>

                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                        <strong>Transaction:</strong>{" "}
                        {order.payments?.data?.explorer_tx_url ? (
                          <a
                            href={order.payments.data.explorer_tx_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {order.payments.data.transaction}
                          </a>
                        ) : (
                          order.payments.data.transaction
                        )}
                      </Typography>

                      {order.payments?.data?.sender && (
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                          <strong>Sender:</strong> {order.payments.data.sender}
                        </Typography>
                      )}

                      {order.payments?.data?.receiver && (
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                          <strong>Receiver:</strong> {order.payments.data.receiver}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                <Box sx={{
                  backgroundColor: theme.palette.mode === 'light'
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  boxShadow: theme.shadows[1]
                }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="primary.contrastText"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      TOTAL AMOUNT
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="primary.contrastText"
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      ${totalPrice.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {/* Desktop Action Buttons */}
                {!isMobile ? (
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    pt: 2,
                    flexWrap: 'wrap'
                  }}>

                    <Button
                      variant="outlined"
                      disabled={isCancelledOrder(order)}
                      onClick={() => navigate(`/orders/${order.order_id}/edit`)}
                      startIcon={<EditIcon />}
                      sx={{
                        borderRadius: 30,
                        px: 3,
                        textTransform: "none",
                        minWidth: { xs: "100%", sm: "auto" },
                      }}
                    >
                      Edit Order
                    </Button>

                    {!isCancelledOrder(order) && getRefundDue(order) > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => openRefundDialog(order)}
                        sx={{
                          borderRadius: 30,
                          px: 3,
                          textTransform: "none",
                          minWidth: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Refund ${getRefundDue(order).toFixed(2)}
                      </Button>
                    )}

                    {canCancelOrder(order) && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => openCancelDialog(order)}
                        startIcon={<CancelIcon />}
                        sx={{
                          borderRadius: 30,
                          px: 3,
                          textTransform: "none",
                          minWidth: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Cancel Order
                      </Button>
                    )}

                    {order.payment_status !== "paid" && (
                      <>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<LocalAtmIcon />}
                          onClick={() => setConfirmPaymentOrder(order)}
                          sx={{
                            borderRadius: 30,
                            px: 3,
                            textTransform: "none",
                            minWidth: { xs: "100%", sm: "auto" },
                          }}
                        >
                          Record Payment
                        </Button>

                        {order.payment_method === "payment_link" && (
                          <Button
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={() => handleResendPaymentLink(order.order_id)}
                            sx={{
                              borderRadius: 30,
                              px: 3,
                              textTransform: "none",
                              minWidth: { xs: "100%", sm: "auto" },
                            }}
                          >
                            Resend Payment Link
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      variant="contained"
                      startIcon={<LocalShippingIcon />}
                      onClick={() => handleFulfill(order.order_id)}
                      sx={{
                        borderRadius: 30,
                        px: 3,
                        textTransform: "none",
                        minWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      Fulfill
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      size="medium"
                      onClick={() => sendReceipt(order.order_id)}
                      sx={{
                        borderRadius: 30,
                        px: 3,
                        textTransform: 'none',
                        minWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      Email Receipt
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PrintIcon />}
                      size="medium"
                      onClick={() => generateOrderPDF(order)}
                      sx={{
                        borderRadius: 30,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme.shadows[2],
                        '&:hover': {
                          boxShadow: theme.shadows[4]
                        },
                        minWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      Print Receipt
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<LocalShippingIcon />}
                      onClick={() => handleFulfill(order.order_id)}
                      sx={{
                        borderRadius: 30,
                        px: 3,
                        textTransform: "none",
                        width: '100%'
                      }}
                    >
                      Fulfill Order
                    </Button>
                  </Box>
                )}
              </AccordionDetails>
            </ModernAccordion>
          );
        })}
      </Box>

      {hasMoreOrders && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={() => setOrdersLimit((n) => n + ORDERS_PAGE_SIZE)}>
            Load older orders
          </Button>
        </Box>
      )}

      {/* Mobile Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem
          disabled={isCancelledOrder(selectedOrder)}
          onClick={() => handleMenuAction("edit-order")}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit Order
        </MenuItem>
        {!isCancelledOrder(selectedOrder) && getRefundDue(selectedOrder) > 0 && (
          <MenuItem onClick={() => handleMenuAction("refund")}>
            Refund ${getRefundDue(selectedOrder).toFixed(2)}
          </MenuItem>
        )}
        {selectedOrder?.payment_status !== "paid" && (
          <MenuItem onClick={() => handleMenuAction("confirm-payment")}>
            <LocalAtmIcon sx={{ mr: 1 }} />
            Record Payment
          </MenuItem>
        )}

        {selectedOrder?.payment_status !== "paid" && selectedOrder?.payment_method === "payment_link" && (
          <MenuItem
            onClick={() => {
              handleResendPaymentLink(selectedOrder.order_id);
              handleActionMenuClose();
            }}
          >
            <RestartAltIcon sx={{ mr: 1 }} />
            Resend Payment Link
          </MenuItem>
        )}
        <MenuItem disabled={isCancelledOrder(selectedOrder)} onClick={() => handleMenuAction('fulfill')}>
          <LocalShippingIcon sx={{ mr: 1 }} />
          Fulfill
        </MenuItem>

        {canCancelOrder(selectedOrder) && (
          <MenuItem onClick={() => handleMenuAction("cancel")}>
            <CancelIcon sx={{ mr: 1 }} />
            Cancel Order
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuAction('email')}>
          <EmailIcon sx={{ mr: 1 }} />
          Email Receipt
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('print')}>
          <PrintIcon sx={{ mr: 1 }} />
          Print Receipt
        </MenuItem>
      </Menu>
      <Dialog
        open={!!cancelOrderTarget}
        onClose={() => {
          if (!cancelLoading) setCancelOrderTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Order</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {cancelOrderTarget && (
              <Alert severity={cancelDraft.force ? "warning" : "info"}>
                Cancelling order #{cancelOrderTarget.order_number}
                {cancelDraft.force
                  ? " requires confirmation because it is already in progress."
                  : "."}
              </Alert>
            )}

            <TextField
              select
              label="Cancellation refund option"
              value={cancelDraft.refund_mode}
              disabled={cancelLoading}
              onChange={(e) =>
                setCancelDraft((prev) => ({
                  ...prev,
                  refund_mode: e.target.value as "none" | "provider" | "manual",
                }))
              }
              fullWidth
            >
              <MenuItem value="none">Cancel only — no refund now</MenuItem>
              {canUseProviderRefund(cancelOrderTarget) && (
                <MenuItem value="provider">Cancel + provider refund</MenuItem>
              )}
              <MenuItem value="manual">Cancel + manual refund record</MenuItem>
            </TextField>

            {cancelDraft.refund_mode !== "none" && (
              <TextField
                label="Refund amount"
                type="number"
                value={cancelDraft.amount}
                disabled={cancelLoading}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                onChange={(e) =>
                  setCancelDraft((prev) => ({ ...prev, amount: e.target.value }))
                }
                fullWidth
              />
            )}

            {cancelDraft.refund_mode === "manual" && (
              <TextField
                select
                label="Manual refund method"
                value={cancelDraft.manual_refund_method}
                disabled={cancelLoading}
                onChange={(e) =>
                  setCancelDraft((prev) => ({
                    ...prev,
                    manual_refund_method: e.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="clover_manual">Clover manual</MenuItem>
                <MenuItem value="paypal_manual">PayPal manual</MenuItem>
                <MenuItem value="etransfer">E-transfer</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            )}

            <TextField
              label="Cancellation reason"
              value={cancelDraft.reason}
              disabled={cancelLoading}
              onChange={(e) =>
                setCancelDraft((prev) => ({ ...prev, reason: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setCancelOrderTarget(null)}
            disabled={cancelLoading}
          >
            Close
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={
              cancelLoading ||
              !cancelDraft.reason ||
              (cancelDraft.refund_mode !== "none" && !cancelDraft.amount)
            }
            onClick={handleSubmitCancelOrder}
          >
            {cancelLoading
              ? "Cancelling..."
              : cancelDraft.force
                ? "Confirm Cancel Order"
                : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!refundOrder}
        onClose={() => {
          if (!refundLoading) setRefundOrder(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Refund</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {refundOrder && (
              <Alert severity="info">
                Refund due for order #{refundOrder.order_number}: $
                {getRefundDue(refundOrder).toFixed(2)}
              </Alert>
            )}

            {refundOrder &&
              !canUseProviderRefund(refundOrder) &&
              ["clover", "card"].includes(String(refundOrder.payment_method || "").toLowerCase()) && (
                <Alert severity="warning">
                  Clover partial refunds are recorded manually because the current Clover iFrame charge flow only supports full provider refunds.
                </Alert>
              )}

            <TextField
              select
              fullWidth
              label="Refund type"
              value={refundMode}
              disabled={refundLoading}
              onChange={(e) => {
                const nextMode = e.target.value as "provider" | "manual";
                setRefundMode(nextMode);

                if (nextMode === "provider" && refundOrder) {
                  setRefundDraft((prev) => ({
                    ...prev,
                    method: String(refundOrder.payment_method || "").toLowerCase(),
                  }));
                }
              }}
            >
              {canUseProviderRefund(refundOrder) && (
                <MenuItem value="provider">Provider refund</MenuItem>
              )}
              <MenuItem value="manual">Manual refund</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={refundDraft.amount}
              disabled={refundLoading}
              inputProps={{
                min: 0,
                step: "0.01",
                max: refundOrder ? getRefundDue(refundOrder) : undefined,
              }}
              onChange={(e) =>
                setRefundDraft((prev) => ({ ...prev, amount: e.target.value }))
              }
            />

            {refundMode === "manual" && (
              <TextField
                select
                fullWidth
                label="Manual refund method"
                value={refundDraft.method}
                disabled={refundLoading}
                onChange={(e) =>
                  setRefundDraft((prev) => ({ ...prev, method: e.target.value }))
                }
              >
                <MenuItem value="clover_manual">Clover manual</MenuItem>
                <MenuItem value="etransfer">E-transfer</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="store_credit" disabled>
                  Store credit — coming soon
                </MenuItem>
                <MenuItem value="crypto_manual" disabled>
                  Crypto — coming soon
                </MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            )}

            <TextField
              fullWidth
              label="Reason"
              value={refundDraft.reason}
              disabled={refundLoading}
              onChange={(e) =>
                setRefundDraft((prev) => ({ ...prev, reason: e.target.value }))
              }
            />

            {refundMode === "manual" && (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Internal note"
                placeholder="Example: Refunded manually in Clover dashboard."
                value={refundDraft.note}
                disabled={refundLoading}
                onChange={(e) =>
                  setRefundDraft((prev) => ({ ...prev, note: e.target.value }))
                }
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setRefundOrder(null)}
            disabled={refundLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={refundLoading || !refundDraft.amount}
            onClick={handleSubmitRefund}
          >
            {refundLoading ? "Processing..." : "Process Refund"}
          </Button>
        </DialogActions>
      </Dialog>
      <FeedbackToast
        {...feedback}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />

      <ConfirmManualPaymentDialog
        open={!!confirmPaymentOrder}
        order={confirmPaymentOrder}
        loading={confirmPaymentLoading}
        onClose={() => {
          if (!confirmPaymentLoading) setConfirmPaymentOrder(null);
        }}
        onConfirm={handleConfirmManualPayment}
      />
    </Box>
  );
};

export default OrdersPage;